/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { token } from '@atlaskit/tokens';
import type { StreamingCommand } from '@atlassian/editor-plugin-ai-streaming-orchestrator';

import type { AiSuggestionsPlugin } from '../../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../../pm-plugins/main';

import { SuggestionCard } from './SuggestionCard';

const containerStyle = css({
	position: 'absolute',
	zIndex: 2,
});

export const SuggestionCardContentComponent = ({
	api,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
}) => {
	const { suggestions, selectedSuggestionId } = useSharedPluginStateWithSelector(
		api,
		['aiSuggestions'],
		(state) => ({
			suggestions: state?.aiSuggestionsState?.suggestions,
			selectedSuggestionId: state?.aiSuggestionsState?.selectedSuggestion,
		}),
	);

	if (selectedSuggestionId === undefined) {
		return;
	}

	const suggestion = suggestions?.find((s) => s.id === selectedSuggestionId);

	if (!suggestion) {
		return;
	}

	const onClose = () => {
		api?.core?.actions.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'SELECT_SUGGESTION',
				id: undefined,
			});
			api?.showDiff?.commands.hideDiff?.({ tr });

			return tr;
		});
	};

	const onDiscard = () => {
		api?.core?.actions.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTION',
				id: selectedSuggestionId,
			});

			api?.showDiff?.commands.hideDiff?.({ tr });

			return tr;
		});
	};

	const onAccept = () => {
		api?.core?.actions.execute(({ tr }) => {
			api?.showDiff?.commands.hideDiff?.({ tr });
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTION',
				id: selectedSuggestionId,
			});
			return tr;
		});

		setTimeout(() => {
			api?.core.actions.execute(({ tr }) => {
				api?.aiStreamingOrchestrator?.actions.startSession();
				suggestion.toolCalls.forEach((toolCall) => {
					const id = api?.aiStreamingOrchestrator?.actions.initAction(
						{
							type: toolCall.name,
							localId: toolCall.arguments.localId,
						} as StreamingCommand,
						{
							hideLoadingScreen: true,
						},
					);
					if (id) {
						api?.aiStreamingOrchestrator?.actions.streamValue(id, toolCall.arguments.value);
						api?.aiStreamingOrchestrator?.actions.finishAction(id, undefined);
					}
				});
				api?.aiStreamingOrchestrator?.actions.endSession();
				return tr;
			});
		}, 0);
	};
	return (
		<span
			css={containerStyle}
			style={{
				top: `anchor(--button-${selectedSuggestionId} start)`,
				left: `calc(anchor(--button-${selectedSuggestionId} end) + ${token('space.150')})`,
			}}
		>
			<SuggestionCard
				suggestion={suggestion?.description}
				onClose={onClose}
				onDiscard={onDiscard}
				onAccept={onAccept}
			/>
		</span>
	);
};
